import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    CategoryModel,
    ConfigConstants,
    InternalInfoModel,
    IValueChanged,
    TextInputComponent,
} from '@evorait/pace-core';
import { Subject, timer } from 'rxjs';

@Component({
    selector: 'pace-internal-info-contact',
    templateUrl: './internal-info-contact.component.html',
    styleUrls: ['./internal-info-contact.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class InternalInfoContactComponent implements OnInit, OnDestroy {
    @Input()
    public model: InternalInfoModel;

    @Input()
    public parentForm: FormArray;

    @Input()
    public categoryModel: CategoryModel;

    @Input()
    public isSaved: boolean;

    @ViewChild('functionName')
    public functionName: TextInputComponent;

    private destroy$ = new Subject<void>();
    public isEmailInvalid = false;
    public contactForm: FormGroup;
    public contactFormChildren: FormArray;
    public constructor(private readonly formBuilder: FormBuilder) {}

    public ngOnInit(): void {
        this.contactFormChildren = this.formBuilder.array([]);
        this.contactForm = this.formBuilder.group({
            children: this.contactFormChildren,
        });
        this.parentForm.push(this.contactForm);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public onValueChange(event: IValueChanged, checkEmailValidity = false): void {
        const selectedContact = this.model.contactModel.selectedEntry;

        if (this.model.contactModel.value.length && selectedContact) {
            selectedContact.model.formValidationStatus = this.contactFormChildren.status;

            this.model.contactModel.options = {
                ...this.model.contactModel.options,
                disabled: this.contactFormChildren.invalid,
            };
        }

        if (event.oldValue !== event.newValue) {
            this.categoryModel.isEdited = true;
            if (checkEmailValidity) {
                const emailText = selectedContact.model.email.textLine;
                this.isEmailInvalid =
                    emailText.value.length > 0 && emailText.value.match(emailText.options.pattern) === undefined;
            }

            this.categoryModel.isContentAvailable = !!this.model.contactModel.value.length;
            this.categoryModel.isInvalid =
                this.model.contactModel.value.length > 0 &&
                selectedContact &&
                selectedContact.model.formValidationStatus === ConfigConstants.FORM_STATUS.INVALID;
            this.model.updateDisplayName();
        }
    }

    public updateFormValidity(shouldEmitEvent = false): void {
        this.contactFormChildren.controls.forEach((control) => {
            control.updateValueAndValidity({ emitEvent: shouldEmitEvent });
        });
    }

    public onAdd(): void {
        this.categoryModel.isEdited = true;
        this.model.createContactModel();
        this.updateContactValidation();
        this.updateFormValidity();
    }

    public updateContactValidation(): void {
        const selectedContact = this.model.contactModel.selectedEntry;
        this.categoryModel.isContentAvailable = !!this.model.contactModel.value.length;
        this.isEmailInvalid =
            selectedContact &&
            selectedContact.model.email.textLine.value.length > 0 &&
            selectedContact.model.email.textLine.value.match(selectedContact.model.email.textLine.options.pattern) ===
                null;
        this.categoryModel.isInvalid =
            this.model.contactModel.value.length > 0 &&
            selectedContact &&
            selectedContact.model.formValidationStatus === ConfigConstants.FORM_STATUS.INVALID;
    }

    public deleteInternalInfoContact(): void {
        this.model.deleteInternalInfoContact();
        this.categoryModel.isEdited = false;
        this.updateContactValidation();
    }

    public onCancel(): void {
        this.categoryModel.isEdited = false;
        this.model.restoreOriginalContactsValues();
        this.updateContactValidation();
    }

    public onEdit(): void {
        this.categoryModel.isEdited = true;

        // In Contact screen we have to focus on "Function name" field
        const focusField = <HTMLInputElement>this.functionName.internalInfoTextAreaInput.nativeElement;

        //  Focus on last character of the string
        const charPosition = focusField.value.length;

        timer(1).subscribe(() => {
            focusField.setSelectionRange(charPosition, charPosition);
            focusField.focus();
        });
    }
}
